import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { useArmControl } from "providers/ArmControlProvider";

/** Built-in Trigger shortcuts — matches arm server BUILTIN_PRESET_NAMES. */
export type ArmBuiltinCommand = "go_zero" | "ready" | "stow";

/** Built-in pose name, or any user-saved preset name from /arm/state. */
export type ArmPresetCommand = ArmBuiltinCommand | (string & {});

export type ArmMissionChoice =
  | { kind: "preset"; command: ArmPresetCommand }
  | { kind: "press_elevator" };

interface ArmMissionDialogProps {
  visible: boolean;
  onSelect: (choice: ArmMissionChoice) => void;
  onClose: () => void;
}

const BUILTIN_PRESET_NAMES = new Set<string>(["go_zero", "ready", "stow"]);

const PRESET_OPTIONS: Array<{ command: ArmBuiltinCommand; labelKey: string }> = [
  { command: "go_zero", labelKey: "dialogs.armGoZero" },
  { command: "ready", labelKey: "dialogs.armReady" },
  { command: "stow", labelKey: "dialogs.armStow" },
];

const ArmMissionDialog: React.FC<ArmMissionDialogProps> = ({
  visible,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation(["missions", "common"]);
  const { presetNames } = useArmControl();

  const savedPresetNames = useMemo(
    () =>
      (presetNames ?? []).filter((name) => !BUILTIN_PRESET_NAMES.has(name)),
    [presetNames],
  );

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="w-80 rounded-r10 border border-black80 bg-black100 p-4"
    >
      <p className="mb-1 text-h5 font-semibold">{t("dialogs.armTitle")}</p>
      <p className="mb-3 text-h6 text-black40">{t("dialogs.armSubtitle")}</p>
      <div className="mb-3 max-h-72 flex flex-col gap-2 overflow-y-auto">
        {PRESET_OPTIONS.map(({ command, labelKey }) => (
          <button
            key={command}
            type="button"
            className="w-full rounded-r5 bg-black70 px-3 py-2.5 text-left text-h5 hover:bg-black60"
            onClick={() => onSelect({ kind: "preset", command })}
          >
            {t(labelKey)}
          </button>
        ))}
        {savedPresetNames.length > 0 && (
          <>
            <p className="mt-1 text-h6 font-semibold text-black40">
              {t("dialogs.armSavedPositions")}
            </p>
            {savedPresetNames.map((name) => (
              <button
                key={name}
                type="button"
                className="w-full rounded-r5 bg-black70 px-3 py-2.5 text-left text-h5 hover:bg-black60"
                onClick={() => onSelect({ kind: "preset", command: name })}
              >
                {name}
              </button>
            ))}
          </>
        )}
        <button
          type="button"
          className="w-full rounded-r5 bg-black70 px-3 py-2.5 text-left text-h5 hover:bg-black60"
          onClick={() => onSelect({ kind: "press_elevator" })}
        >
          {t("dialogs.armPressElevator")}
        </button>
      </div>
      <div className="flex justify-end">
        <FunctionalButton
          title={t("common:cancel")}
          titleClassName="text-black60"
          className="cancel-button px-4 py-2"
          onClick={onClose}
        />
      </div>
    </AnimatedModal>
  );
};

export default ArmMissionDialog;
